/**
 * Helper utilities for preserving and restoring user context (status, page, search, tab)
 * across page refreshes, navigation, and action completions, while cleanly resetting to
 * default status tab when switching between different modules.
 */

export const CONTEXT_STORAGE_PREFIX = "admin_module_context_";
export const ACTIVE_MODULE_STORAGE_KEY = "admin_active_module_group";

const isTestEnv =
  typeof process !== "undefined" &&
  (process.env.NODE_ENV === "test" || Boolean(process.env.JEST_WORKER_ID));

let isInitialDocLoad = true;

export const isPageReload = () => {
  try {
    if (typeof window === "undefined" || !window.performance) return false;
    if (!isInitialDocLoad) return false;

    if (window.__MOCK_RELOAD__ !== undefined) {
      return Boolean(window.__MOCK_RELOAD__);
    }

    const navEntries = window.performance.getEntriesByType?.("navigation");
    if (navEntries && navEntries.length > 0) {
      return navEntries[0].type === "reload";
    }
    return window.performance.navigation?.type === 1;
  } catch (e) {
    return false;
  }
};

export const markInitialDocLoadComplete = () => {
  isInitialDocLoad = false;
};

export const resetInitialDocLoad = () => {
  isInitialDocLoad = true;
};

if (typeof window !== "undefined" && !isTestEnv) {
  setTimeout(() => {
    isInitialDocLoad = false;
  }, 1000);
}

export const MODULE_GROUPS = {
  ENQUIRY: "enquiryMgmt",
  BUSINESS: "business",
  INVESTOR: "investor",
  ENTERPRISE: "enterprise",
  CREDIT_LIMIT: "creditLimit",
  DEALS: "dealsManagement",
};

export const getModuleGroup = (moduleKey) => {
  if (!moduleKey || typeof moduleKey !== "string") return null;
  if (moduleKey === "enquiryMgmt") return "enquiryMgmt";
  if (moduleKey === "business") return "business";
  if (moduleKey === "investor") return "investor";
  if (moduleKey === "enterprise") return "enterprise";
  if (moduleKey === "creditLimit" || moduleKey === "creditLimitRequests") return "creditLimit";
  if (
    moduleKey === "dealsManagement" ||
    moduleKey === "deals_JV" ||
    moduleKey === "deals" ||
    moduleKey.startsWith("deals_")
  ) {
    return "dealsManagement";
  }
  return moduleKey;
};

export const getModuleGroupFromPath = (path) => {
  if (!path || typeof path !== "string") return null;
  const clean = path.toLowerCase().trim().split("?")[0].split("#")[0];
  if (clean.startsWith("/enquiry-management")) return "enquiryMgmt";
  if (clean.startsWith("/business")) return "business";
  if (clean.startsWith("/investor")) return "investor";
  if (clean.startsWith("/enterprise")) return "enterprise";
  if (clean.startsWith("/credit-limit")) return "creditLimit";
  if (
    clean.startsWith("/deals-management") ||
    clean.startsWith("/deals") ||
    clean.startsWith("/joint-ventures-deals") ||
    clean.startsWith("/deal-approval") ||
    clean.startsWith("/joint-ventures-view-details")
  ) {
    return "dealsManagement";
  }

  const segment = clean.replace(/^\//, "").split("/")[0];
  return segment || null;
};

export const getActiveModuleGroup = () => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    return window.sessionStorage.getItem(ACTIVE_MODULE_STORAGE_KEY);
  } catch (e) {
    return null;
  }
};

export const setActiveModuleGroup = (group) => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    if (group) {
      window.sessionStorage.setItem(ACTIVE_MODULE_STORAGE_KEY, group);
    } else {
      window.sessionStorage.removeItem(ACTIVE_MODULE_STORAGE_KEY);
    }
  } catch (e) {}
};

export const getSavedContext = (moduleKey) => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    if (isTestEnv) return null;
    const raw = window.sessionStorage.getItem(`${CONTEXT_STORAGE_PREFIX}${moduleKey}`);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};


export const saveContext = (moduleKey, data) => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    const existing = getSavedContext(moduleKey) || {};
    const updated = { ...existing, ...data };
    window.sessionStorage.setItem(
      `${CONTEXT_STORAGE_PREFIX}${moduleKey}`,
      JSON.stringify(updated)
    );
  } catch (e) {}
};

export const clearSavedContext = (moduleKey) => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    window.sessionStorage.removeItem(`${CONTEXT_STORAGE_PREFIX}${moduleKey}`);
  } catch (e) {}
};

export const clearOtherModuleContexts = (currentGroup) => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    const keysToRemove = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key && key.startsWith(CONTEXT_STORAGE_PREFIX)) {
        const modKey = key.replace(CONTEXT_STORAGE_PREFIX, "");
        if (getModuleGroup(modKey) !== currentGroup) {
          keysToRemove.push(key);
        }
      }
    }
    keysToRemove.forEach((k) => window.sessionStorage.removeItem(k));
  } catch (e) {}
};

export const clearAllSavedContexts = () => {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    const keysToRemove = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key && key.startsWith(CONTEXT_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => window.sessionStorage.removeItem(k));
  } catch (e) {}
};

export const getUrlParams = () => {
  try {
    if (typeof window === "undefined" || !window.location) return {};
    const params = new URLSearchParams(window.location.search);
    const result = {};
    for (const [key, value] of params.entries()) {
      result[key] = value;
    }
    return result;
  } catch (e) {
    return {};
  }
};

export const updateUrlParams = (paramsToUpdate) => {
  try {
    if (typeof window === "undefined" || !window.location || !window.history) return;
    if (isTestEnv) return;
    const url = new URL(window.location.href);

    Object.entries(paramsToUpdate).forEach(([key, value]) => {
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      ) {
        url.searchParams.delete(key);
      } else if (key === "page" && (Number(value) <= 1 || isNaN(Number(value)))) {
        url.searchParams.delete(key);
      } else if (Array.isArray(value)) {
        url.searchParams.set(key, value.join(","));
      } else {
        url.searchParams.set(key, String(value));
      }
    });

    const newSearch = url.searchParams.toString();
    const newPath = url.pathname + (newSearch ? `?${newSearch}` : "") + url.hash;
    const currentSearch = window.location.search.replace(/^\?/, "");

    if (currentSearch !== newSearch) {
      window.history.replaceState(window.history.state, "", newPath);
    }
  } catch (e) {}
};
